import { useEffect } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import Navbar from "./Nav";
import useLocalStorage from "helpers/useLocalStorage";
import {
  clearAuthSession,
  getTokenExpirationTime,
  isTokenExpired,
  MAX_TOKEN_EXPIRY_TIMEOUT_MS,
} from "helpers/authSession";

export default function MainLayout() {
  const [token] = useLocalStorage("token", "");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      return;
    }

    const expirationTime = getTokenExpirationTime(token);

    const redirectToLogin = () => {
      clearAuthSession();
      navigate("/login", { replace: true });
    };

    if (!expirationTime || Date.now() >= expirationTime) {
      redirectToLogin();
      return;
    }

    let timeoutId: number | undefined;

    const scheduleExpirationCheck = () => {
      const timeLeft = expirationTime - Date.now();

      if (timeLeft <= 0) {
        redirectToLogin();
        return;
      }

      timeoutId = window.setTimeout(
        scheduleExpirationCheck,
        Math.min(timeLeft, MAX_TOKEN_EXPIRY_TIMEOUT_MS),
      );
    };

    scheduleExpirationCheck();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [navigate, token]);

  if (!token || isTokenExpired(token)) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-shell h-screen overflow-hidden bg-[#f8fafc] transition-colors dark:bg-slate-950">
      <div className="grid h-screen w-full min-w-0 grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[auto_minmax(0,1fr)]">
        <div className="contents md:block md:overflow-auto md:w-full">
          <SideBar />
        </div>
        <div className="app-main relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-[#f8fafc] transition-colors dark:bg-slate-950">
          <div className="sticky top-0 z-40 flex-none">
            <Navbar />
          </div>
          <div className="min-h-0 w-full min-w-0 flex-1 overflow-hidden pb-24 md:pb-0">
            <div className="h-full min-w-0 overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-5 sm:py-4">
              <div className="app-content-surface h-full w-full min-w-0">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
